import React, { useState, useEffect } from "react";
// FIX 1: Removed 'Card' from imports to clear the ESLint warning
import { Table, Button, Form, Modal, Row, Col, Badge } from "react-bootstrap";
import { useCTISSchema } from "./CTISSchemaProvider";
import { validateItem } from "../../validators/CTISItemValidator";

export default function CTISItemRegisterPage() {
  const { screenIndex, loading } = useCTISSchema();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [currentItem, setCurrentItem] = useState({
    id: "",
    ctis_section: "PART_I",
    item_name: "",
    item_type: "document",
    mandatory: true,
    publishable: true,
    public_version_required: true,
    restricted_version_allowed: true,
    per_msc: false,
    source_chapter: "",
    status: "active"
  });

  // Relationship Awareness: Sync ID prefix
  useEffect(() => {
    // FIX 2: Check only currentItem.ctis_section to avoid the dependency loop warning
    const prefix = `001.01 CTIS_${currentItem.ctis_section}_`;
    if (!currentItem.id.startsWith(prefix)) {
      setCurrentItem(prev => ({ ...prev, id: prefix }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem.ctis_section]); 

  const handleSave = () => {
    const result = validateItem(currentItem, items);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }
    setItems(prev => [...prev.filter(i => i.id !== currentItem.id), currentItem]);
    setShowModal(false);
  };

  const openAddModal = () => {
    setCurrentItem({
      id: "",
      ctis_section: "PART_I",
      item_name: "",
      item_type: "document",
      mandatory: true,
      publishable: true,
      public_version_required: true,
      restricted_version_allowed: true,
      per_msc: false,
      source_chapter: "",
      status: "active"
    });
    setErrors({});
    setShowModal(true);
  };

  // FIX 3: Robust loading check. If loading is done but screenIndex is missing, 
  // show an error instead of hanging forever.
  if (loading) return <div className="p-5 text-center text-muted">Loading EMA Registry...</div>;
  if (!screenIndex) return <div className="p-5 text-center text-danger">Error: Could not load EMA Index. Check _screen_index.json path.</div>;

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0 fw-bold">CTIS Item Register</h1>
          <p className="text-muted">Master inventory governed by dynamic EMA Blueprint rules.</p>
        </div>
        <Button variant="dark" onClick={openAddModal}>+ Add Register Item</Button>
      </div>

      <Table hover responsive className="border bg-white shadow-sm">
        <thead className="table-dark">
          <tr>
            <th>ID / Section</th>
            <th>Item Name</th>
            <th>Type</th>
            <th>Regulatory Logic</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td>
                <div className="text-monospace small fw-bold text-primary">{item.id}</div>
                <Badge bg="secondary">{item.ctis_section}</Badge>
              </td>
              <td><strong>{item.item_name}</strong></td>
              <td>{item.item_type}</td>
              <td>
                {item.mandatory && <Badge bg="danger" className="me-1">Mandatory</Badge>}
                {item.publishable && <Badge bg="success" className="me-1">Publishable</Badge>}
              </td>
              <td>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-decoration-none" 
                  onClick={() => { setCurrentItem(item); setShowModal(true); }}
                >
                  Edit
                </Button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan="5" className="text-center py-5 text-muted">Registry is empty.</td>
            </tr>
          )}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold">CTIS Item Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">1. EMA Blueprint Section</Form.Label>
                  <Form.Select 
                    value={currentItem.ctis_section}
                    onChange={e => setCurrentItem(prev => ({...prev, ctis_section: e.target.value}))}
                  >
                    {screenIndex.screens.map(s => (
                      <option key={s.screen_id} value={s.screen_id}>
                        {s.screen_id.toUpperCase()} - {s.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">2. System Item ID</Form.Label>
                  <Form.Control 
                    value={currentItem.id} 
                    isInvalid={!!errors.id}
                    onChange={e => setCurrentItem(prev => ({...prev, id: e.target.value.toUpperCase()}))} 
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">Item Name</Form.Label>
                  <Form.Control 
                    value={currentItem.item_name}
                    isInvalid={!!errors.item_name}
                    placeholder="e.g. Protocol"
                    onChange={e => setCurrentItem(prev => ({...prev, item_name: e.target.value}))} 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold text-uppercase small">Item Type</Form.Label>
                  <Form.Select 
                    value={currentItem.item_type}
                    onChange={e => setCurrentItem(prev => ({...prev, item_type: e.target.value}))}
                  >
                    <option value="document">Document</option>
                    <option value="data_field">Data Field</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="dark" onClick={handleSave}>Save to Registry</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}